import type { Card } from '../types';
import { Deck } from '../game/Deck';

export class CardUI {
  private deck: Deck;
  private onCardSelect: (card: Card, index: number) => void;
  
  constructor(deck: Deck, onCardSelect: (card: Card, index: number) => void) {
    this.deck = deck;
    this.onCardSelect = onCardSelect;
  }
  
  render(container: HTMLElement): void {
    const topCards = this.deck.getTopCards(4);
    
    if (topCards.length === 0) {
      const emptyMessage = document.createElement('div');
      emptyMessage.style.cssText = `
        color: #888;
        font-size: 18px;
        text-align: center;
      `;
      emptyMessage.textContent = 'No cards remaining. Entering Regrets phase...';
      container.appendChild(emptyMessage);
      return;
    }
    
    const cardsContainer = document.createElement('div');
    cardsContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(280px, 22vw), 1fr));
      gap: clamp(15px, 2vw, 25px);
      width: 100%;
      max-width: 1400px;
      padding: clamp(15px, 2vw, 20px);
      justify-items: center;
    `;
    
    topCards.forEach((card, index) => {
      const cardElement = this.createCardElement(card, index);
      cardsContainer.appendChild(cardElement);
    });
    
    container.appendChild(cardsContainer);
  }
  
  private createCardElement(card: Card, index: number): HTMLElement {
    const cardDiv = document.createElement('div');
    cardDiv.style.cssText = `
      width: 100%;
      max-width: min(320px, 25vw);
      min-height: clamp(240px, 22vh, 280px);
      background: 
        linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%),
        radial-gradient(circle at top right, rgba(74, 159, 255, 0.1) 0%, transparent 70%);
      border: 2px solid rgba(255, 255, 255, 0.15);
      border-radius: clamp(12px, 1.2vw, 16px);
      padding: clamp(15px, 2vw, 25px);
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 
        0 8px 16px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      gap: clamp(10px, 1.5vh, 15px);
      position: relative;
      backdrop-filter: blur(10px);
    `;
    
    // Card number badge (top-left corner) - more prominent
    const numberBadge = document.createElement('div');
    numberBadge.style.cssText = `
      position: absolute;
      top: clamp(-10px, -1vh, -12px);
      left: clamp(-10px, -1vw, -12px);
      width: clamp(32px, 3vw, 40px);
      height: clamp(32px, 3vw, 40px);
      background: linear-gradient(135deg, #4a9eff 0%, #2a6fcc 100%);
      border: clamp(2px, 0.3vw, 3px) solid rgba(255, 255, 255, 0.4);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: clamp(16px, 1.8rem, 20px);
      font-weight: bold;
      color: #fff;
      box-shadow: 
        0 4px 8px rgba(0, 0, 0, 0.5),
        0 0 0 2px rgba(0, 0, 0, 0.3);
      z-index: 10;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    `;
    numberBadge.textContent = (index + 1).toString();
    cardDiv.appendChild(numberBadge);
    
    // Hover effect - more dramatic
    cardDiv.addEventListener('mouseenter', () => {
      cardDiv.style.transform = 'translateY(-15px) scale(1.03)';
      cardDiv.style.borderColor = 'rgba(74, 159, 255, 0.5)';
      cardDiv.style.boxShadow = `
        0 12px 24px rgba(0, 0, 0, 0.6),
        0 0 30px rgba(74, 159, 255, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.2)
      `;
      numberBadge.style.transform = 'scale(1.1)';
      numberBadge.style.boxShadow = `
        0 6px 12px rgba(0, 0, 0, 0.6),
        0 0 20px rgba(74, 159, 255, 0.4),
        0 0 0 2px rgba(0, 0, 0, 0.3)
      `;
    });
    
    cardDiv.addEventListener('mouseleave', () => {
      cardDiv.style.transform = 'translateY(0) scale(1)';
      cardDiv.style.borderColor = 'rgba(255, 255, 255, 0.15)';
      cardDiv.style.boxShadow = `
        0 8px 16px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.1)
      `;
      numberBadge.style.transform = 'scale(1)';
      numberBadge.style.boxShadow = `
        0 4px 8px rgba(0, 0, 0, 0.5),
        0 0 0 2px rgba(0, 0, 0, 0.3)
      `;
    });
    
    // Click handler
    cardDiv.addEventListener('click', () => {
      this.onCardSelect(card, index);
    });
    
    // Card name
    const nameElem = document.createElement('div');
    nameElem.style.cssText = `
      font-size: clamp(16px, 1.8rem, 20px);
      font-weight: bold;
      color: #fff;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding-bottom: clamp(8px, 1vh, 10px);
      padding-top: clamp(3px, 0.5vh, 5px);
    `;
    nameElem.textContent = card.name;
    
    // Card description
    const descElem = document.createElement('div');
    descElem.style.cssText = `
      font-size: clamp(14px, 1.5rem, 18px);
      color: #ccc;
      line-height: 1.5;
      flex: 1;
    `;
    descElem.textContent = card.description;
    
    // Options preview
    const optionsPreview = document.createElement('div');
    optionsPreview.style.cssText = `
      font-size: clamp(12px, 1.3rem, 16px);
      color: #888;
      margin-top: clamp(8px, 1vh, 10px);
      padding-top: clamp(8px, 1vh, 10px);
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    `;
    optionsPreview.textContent = `${card.options.length} option${card.options.length !== 1 ? 's' : ''} available`;
    
    cardDiv.appendChild(nameElem);
    cardDiv.appendChild(descElem);
    cardDiv.appendChild(optionsPreview);
    
    return cardDiv;
  }
}

